import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import { memo, useCallback, useMemo } from "react";
import styles from "./ConversationListModal.module.css";

import type { ConversationMetadata } from "@mirage/service-conversation/types";
import type { ComposeSession } from "@mirage/shared/compose/compose-session";
import i18n from "@mirage/translations";
import { ConversationRow, LegacySessionRow } from "./ConversationListRows";

interface ConversationListModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversations?: ConversationMetadata[];
  onClickConversation: (conversation: ConversationMetadata) => void;
  onDeleteConversation: (conversation: ConversationMetadata) => void;
  sortedLegacySessions: ComposeSession[];
  handleLegacySessionClick: (session: ComposeSession) => void;
}

type ConversationGroup = {
  title: string;
  conversations: ConversationMetadata[];
};

export const ConversationListModal = memo(
  ({
    isOpen,
    onClose,
    conversations = [],
    onClickConversation,
    onDeleteConversation,
    sortedLegacySessions,
    handleLegacySessionClick,
  }: ConversationListModalProps) => {
    const groupedConversations = useMemo(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const groups: ConversationGroup[] = [
        {
          title: i18n.t("assist_conversation_list_modal_title_today"),
          conversations: [],
        },
        {
          title: i18n.t("assist_conversation_list_modal_title_yesterday"),
          conversations: [],
        },
        {
          title: i18n.t(
            "assist_conversation_list_modal_title_previous_30_days",
          ),
          conversations: [],
        },
      ];
      conversations.forEach((conversation) => {
        const timestamp = new Date(conversation.created);
        if (timestamp >= today) {
          groups[0].conversations.push(conversation);
        } else if (timestamp >= yesterday) {
          groups[1].conversations.push(conversation);
        } else if (timestamp >= thirtyDaysAgo) {
          groups[2].conversations.push(conversation);
        }
      });
      return groups.filter((group) => group.conversations.length > 0);
    }, [conversations]);

    const handleClickConversation = useCallback(
      (conversation: ConversationMetadata) => {
        onClickConversation(conversation);
        onClose();
      },
      [onClickConversation, onClose],
    );

    return (
      <Modal
        open={isOpen}
        isCentered
        width={700}
        withCloseButton="close"
        shouldCloseOnOverlayClick
        onRequestClose={onClose}
      >
        <Modal.Header>
          <Modal.Title size="medium">
            {i18n.t("assist_conversation_list_modal_title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.ModalBody}>
          {groupedConversations.map((group) => (
            <div key={group.title} className={styles.ConversationGroup}>
              <Text
                variant="label"
                className={styles.ConversationGroupTitle}
                isBold
                size="small"
                tagName="h2"
                color="subtle"
              >
                {group.title}
              </Text>
              <div className={styles.ConversationList}>
                {group.conversations.map((conversation) => (
                  <ConversationRow
                    key={conversation.serverConversationId}
                    conversation={conversation}
                    isSelected={false}
                    onClickConversation={handleClickConversation}
                    onDeleteConversation={onDeleteConversation}
                    largeDisplay
                  />
                ))}
              </div>
            </div>
          ))}
          {/* Legacy Sessions Group */}
          {sortedLegacySessions.length > 0 && (
            <div className={styles.ConversationGroup}>
              <Text
                variant="label"
                className={styles.ConversationGroupTitle}
                isBold
                size="small"
                tagName="h2"
                color="subtle"
              >
                {i18n.t("older_than_30_days")}
              </Text>
              <div className={styles.ConversationList}>
                {sortedLegacySessions.map((session: ComposeSession) => (
                  <LegacySessionRow
                    key={session.id}
                    session={session}
                    onClickSession={handleLegacySessionClick}
                    largeDisplay
                    onClose={onClose}
                  />
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  },
);
ConversationListModal.displayName = "ConversationListModal";
